import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { db } from "../../../../firebase";
import {
  collection,
  getDocs,
  query,
  where,
  doc,
  updateDoc,
  getDoc,
} from "firebase/firestore";
import { getAuth, onAuthStateChanged, signOut } from "firebase/auth";

export default function CorporateDashboard() {
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editingField, setEditingField] = useState(null);
  const [tempValues, setTempValues] = useState({});
  const [dirtyFields, setDirtyFields] = useState(new Set());
  const [verificationCode, setVerificationCode] = useState("");
  const [userCodeInput, setUserCodeInput] = useState("");
  const [updateLoading, setUpdateLoading] = useState(false);
  const [authorizedPerson, setAuthorizedPerson] = useState(null);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  // 🧩 Oturum ve veri kontrolü - SIGNUP YAPISINA GÖRE GÜNCELLENDİ
  useEffect(() => {
    const auth = getAuth();

    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        navigate("/login_corporate", { replace: true });
        return;
      }

      const stored = localStorage.getItem("companyData");
      if (!stored) {
        await signOut(auth);
        navigate("/login_corporate", { replace: true });
        return;
      }

      const parsed = JSON.parse(stored);
      const email = parsed?.email;
      const taxNumber = parsed?.taxNumber;
      
      if (!email || !taxNumber) {
        await signOut(auth);
        navigate("/login_corporate", { replace: true });
        return;
      }

      try {
        // 1. Şirket verisini vergi numarasına göre çek
        const companiesRef = collection(db, "companies");
        const q = query(
          companiesRef, 
          where("taxInfo.taxNumber", "==", Number(taxNumber))
        );
        const snapshot = await getDocs(q);

        if (snapshot.empty) {
          console.warn("Şirket bulunamadı.");
          await signOut(auth);
          navigate("/login_corporate", { replace: true });
          return;
        }

        const docSnap = snapshot.docs[0];
        const data = docSnap.data();

        // 2. Yetkili kişi bilgilerini çek (signup'da kaydedildi)
        let personData = null;
        try {
          const personRef = doc(db, "companies", docSnap.id, "authorized_person", "personel_info");
          const personSnap = await getDoc(personRef);
          if (personSnap.exists()) {
            personData = personSnap.data();
          }
        } catch (error) {
          console.warn("Yetkili kişi bilgisi bulunamadı:", error);
        }

        setAuthorizedPerson(personData);

        // Signup'da kaydedilen temel veriler
        const companyData = {
          id: docSnap.id,
          company_id: data.company_id || "",
          taxInfo: data.taxInfo || {
            taxNumber: Number(taxNumber),
            taxCity: "",
            taxOfficeName: "",
            companyType: "",
          },
          // SIGNUP'DA KAYDEDİLEN TEMEL ALANLAR
          email: data.email || email || "",
          // Diğer alanlar signup'da kaydedilmediği için boş olacak
          company_name: data.company_name || data.company_id || "", // company_id'yi company_name olarak kullan
          description: data.description || "",
          phone: data.phone || (personData?.person_phone || ""),
          location: data.location || "",
          website: data.website || "",
          social_media: data.social_media || {
            linkedin: "",
            facebook: "",
            instagram: "",
          },
        };

        setCompany(companyData);

        // Temp values'ı güncelle - eksik alanlar için boş string
        setTempValues({
          company_name: data.company_name || data.company_id || "",
          description: data.description || "",
          email: data.email || email || "",
          phone: data.phone || (personData?.person_phone || ""),
          location: data.location || "",
          website: data.website || "",
          linkedin: data.social_media?.linkedin || "",
          facebook: data.social_media?.facebook || "",
          instagram: data.social_media?.instagram || "",
        });
      } catch (error) {
        console.error("Veri çekme hatası:", error);
      } finally {
        setLoading(false);
      }
    });

    return () => unsubscribe();
  }, [navigate]);

  // 🔹 Satıra tıklayıp düzenleme başlatma
  const handleRowClick = (field) => {
    setEditingField(field);
    setTimeout(() => {
      inputRef.current?.focus();
      const el = inputRef.current;
      if (el) {
        const val = el.value;
        el.value = "";
        el.value = val;
      }
    }, 0);
  };

  const handleTempChange = (field, value) => {
    setTempValues((prev) => ({ ...prev, [field]: value }));
    setDirtyFields((prevSet) => new Set(prevSet).add(field));
  };

  const finishEditing = () => setEditingField(null);

  const startVerification = () => {
    if (dirtyFields.size === 0) {
      alert("Güncellenecek bir alan yok.");
      return;
    }
    const code = Math.floor(100000 + Math.random() * 900000).toString();
    setVerificationCode(code);
    setUserCodeInput("");
  };

  const handleUpdateConfirm = async () => {
    if (verificationCode === "" || userCodeInput.trim() === "") {
      alert("Lütfen önce kod oluşturup ardından alana giriniz.");
      return;
    }
    if (userCodeInput.trim() !== verificationCode) {
      alert("Kod doğrulaması başarısız. Lütfen doğru kodu girin.");
      return;
    }

    if (!company || !company.id) {
      alert("Şirket bilgisi bulunamadı.");
      return;
    }

    setUpdateLoading(true);

    try {
      const companyRef = doc(db, "companies", company.id);
      const updatePayload = {};
      const social = {};
      const socialKeys = ["linkedin", "facebook", "instagram"];

      dirtyFields.forEach((field) => {
        if (socialKeys.includes(field)) {
          social[field] = tempValues[field] || "";
        } else {
          updatePayload[field] = tempValues[field] || "";
        }
      });

      if (Object.keys(social).length > 0) {
        updatePayload.social_media = {
          ...(company.social_media || {}),
          ...social,
        };
      }

      await updateDoc(companyRef, updatePayload);

      setCompany((prev) => ({
        ...prev,
        ...Object.fromEntries(
          Object.entries(updatePayload).filter(([k]) => k !== "social_media")
        ),
        social_media: {
          ...(prev?.social_media || {}),
          ...(updatePayload.social_media || {}),
        },
      }));

      setDirtyFields(new Set());
      setVerificationCode("");
      setUserCodeInput("");
      alert("Bilgiler başarıyla güncellendi.");
    } catch (err) {
      console.error("Güncelleme hatası:", err);
      alert("Güncelleme sırasında hata oluştu.");
    } finally {
      setUpdateLoading(false);
    }
  };

  const handleLogout = async () => {
    const auth = getAuth();
    await signOut(auth);
    localStorage.removeItem("companyData");
    localStorage.removeItem("loginTimestamp");
    navigate("/login_corporate", { replace: true });
  };

  if (loading)
    return (
      <div style={styles.loadingContainer}>
        <div style={styles.spinner}></div>
        <p style={styles.loadingText}>Yükleniyor...</p>
      </div>
    );

  if (!company)
    return (
      <div style={styles.errorContainer}>
        <p style={styles.errorText}>Şirket bilgisi bulunamadı.</p>
        <button 
          style={styles.backButton}
          onClick={() => navigate("/login_corporate")}
        >
          Giriş Sayfasına Dön
        </button>
      </div>
    );

  const fields = [
    { label: "Şirket Adı", key: "company_name" },
    { label: "Açıklama", key: "description" },
    { label: "E-posta", key: "email" },
    { label: "Telefon", key: "phone" },
    { label: "Konum", key: "location" },
    { label: "Web Sitesi", key: "website" },
  ];

  const socialFields = [
    { label: "LinkedIn", key: "linkedin" },
    { label: "Facebook", key: "facebook" },
    { label: "Instagram", key: "instagram" },
  ];

  // Yetkili kişi bilgileri (signup'dan gelen)
  const personFields = authorizedPerson ? [
    { label: "Yetkili Ad Soyad", value: authorizedPerson.person_name || "—" },
    { label: "Yetkili E-posta", value: authorizedPerson.person_email || "—" },
    { label: "Yetkili Telefon", value: authorizedPerson.person_phone || "—" },
    { label: "İl", value: authorizedPerson.city || "—" },
    { label: "İlçe", value: authorizedPerson.district || "—" },
    { label: "Mahalle", value: authorizedPerson.neighborhood || "—" },
    { label: "TC Kimlik No", value: authorizedPerson.identificationNo || "—" },
  ] : [];

  return (
    <div style={styles.mainContainer}>
      <div style={styles.container}>
        {/* Üst Bilgi ve Logout */}
        <div style={styles.header}>
          <div style={styles.headerInfo}>
            <h2 style={styles.heading}>{tempValues.company_name || company.company_id || "Şirket"}</h2>
            <div style={styles.companyInfo}>
              <div style={styles.infoRow}>
                <span style={styles.infoLabel}>Vergi No:</span>
                <span style={styles.infoValue}>{company.taxInfo?.taxNumber || "—"}</span>
              </div>
              <div style={styles.infoRow}>
                <span style={styles.infoLabel}>Şirket Türü:</span>
                <span style={styles.infoValue}>{company.taxInfo?.companyType || "—"}</span>
              </div>
              {company.taxInfo?.taxCity && (
                <div style={styles.infoRow}>
                  <span style={styles.infoLabel}>Vergi Dairesi:</span>
                  <span style={styles.infoValue}>
                    {company.taxInfo.taxCity} / {company.taxInfo.taxOfficeName}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Bilgilendirme kutusu */}


        {/* Yetkili Kişi Bilgileri (Sadece okunabilir) */}
        {personFields.length > 0 && (
          <div style={styles.personSection}>
            <h3 style={styles.sectionTitle}>Yetkili Kişi Bilgileri</h3>
            <div style={styles.personInfoBox}>
              {personFields.map((field, index) => (
                <div key={index} style={styles.personField}>
                  <div style={styles.personLabel}>{field.label}:</div>
                  <div style={styles.personValue}>{field.value}</div>
                </div>
              ))}
            </div>
            <div style={styles.note}>
              <span style={styles.noteIcon}>ℹ️</span>
              Bu bilgiler kayıt sırasında girilmiştir ve buradan değiştirilemez.
            </div>
          </div>
        )}

        {/* Şirket Bilgileri (Düzenlenebilir) */}
        <div style={styles.section}>
          <h3 style={styles.sectionTitle}>Şirket Bilgileri</h3>
                  <div style={styles.infoBanner}>
          <span style={styles.infoIcon}>⚠️</span>
          <span>
            Kayıt olduktan sonra şirket bilgilerinizi buradan tamamlayabilirsiniz.
            Bilgileri değiştirmek için üzerine tıklayın ve "Güncelle" butonunu kullanın.
          </span>
        </div>
          <div style={styles.infoBox}>
            {fields.map(({ label, key }) => {
              const isEditing = editingField === key;
              const displayValue = (tempValues[key] ?? "").toString();
              const isDirty = dirtyFields.has(key);
              return (
                <div
                  key={key}
                  style={{
                    ...styles.fieldRow,
                    backgroundColor: isEditing ? "#fbfcfd" : "transparent",
                  }}
                  onClick={() => handleRowClick(key)}
                >
                  <div style={styles.labelWrap}>
                    <div style={styles.label}>{label}</div>
                    {isDirty && <div style={styles.dirtyPill}>Değişti</div>}
                  </div>

                  <div style={styles.valueWrap}>
                    {isEditing ? (
                      <input
                        ref={inputRef}
                        style={styles.inlineInput}
                        value={displayValue}
                        onChange={(e) => handleTempChange(key, e.target.value)}
                        onBlur={finishEditing}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") finishEditing();
                          else if (e.key === "Escape") {
                            setTempValues((prev) => ({
                              ...prev,
                              [key]: company[key] || "",
                            }));
                            setEditingField(null);
                          }
                        }}
                      />
                    ) : (
                      <div style={styles.valueText}>
                        {displayValue || <span style={styles.emptyText}>Belirtilmemiş</span>}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}

            {/* Sosyal Medya */}
            <div style={{ marginTop: 14 }}>
              <div style={styles.sectionSubtitle}>Sosyal Medya</div>
              {socialFields.map(({ label, key }) => {
                const isEditing = editingField === key;
                const displayValue = (tempValues[key] ?? "").toString();
                const isDirty = dirtyFields.has(key);
                return (
                  <div
                    key={key}
                    style={{
                      ...styles.fieldRow,
                      backgroundColor: isEditing ? "#fbfcfd" : "transparent",
                    }}
                    onClick={() => handleRowClick(key)}
                  >
                    <div style={styles.labelWrap}>
                      <div style={styles.label}>{label}</div>
                      {isDirty && <div style={styles.dirtyPill}>Değişti</div>}
                    </div>

                    <div style={styles.valueWrap}>
                      {isEditing ? (
                        <input
                          ref={inputRef}
                          style={styles.inlineInput}
                          value={displayValue}
                          onChange={(e) => handleTempChange(key, e.target.value)}
                          onBlur={finishEditing}
                        />
                      ) : (
                        <div style={styles.valueText}>
                          {displayValue ? (
                            <a
                              href={displayValue}
                              target="_blank"
                              rel="noreferrer noopener"
                              style={styles.link}
                            >
                              {displayValue}
                            </a>
                          ) : (
                            <span style={styles.emptyText}>Belirtilmemiş</span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Güncelleme Alanı */}
            <div style={styles.updateArea}>
              <button
                style={{
                  ...styles.updateButton,
                  opacity: dirtyFields.size > 0 ? 1 : 0.6,
                  cursor: dirtyFields.size > 0 ? "pointer" : "not-allowed",
                }}
                onClick={startVerification}
                disabled={dirtyFields.size === 0}
              >
                Güncelle
              </button>

              {verificationCode && (
                <div style={styles.verificationBox}>
                  <div style={styles.verificationRow}>
                    <div style={styles.verificationCodeSection}>
                      <div style={styles.smallNote}>
                        Doğrulama kodu (manuel olarak girin):
                      </div>
                      <div style={styles.codeBadge}>{verificationCode}</div>
                    </div>

                    <div style={styles.verificationInputSection}>
                      <input
                        placeholder="Kodu giriniz"
                        value={userCodeInput}
                        onChange={(e) => setUserCodeInput(e.target.value)}
                        style={styles.codeInput}
                      />
                      <div style={styles.verificationButtons}>
                        <button
                          style={{
                            ...styles.confirmButton,
                            opacity: updateLoading ? 0.7 : 1,
                          }}
                          onClick={handleUpdateConfirm}
                          disabled={updateLoading}
                        >
                          {updateLoading
                            ? "Güncelleniyor..."
                            : "Onayla ve Kaydet"}
                        </button>
                        <button
                          style={styles.cancelButton}
                          onClick={() => {
                            setVerificationCode("");
                            setUserCodeInput("");
                          }}
                        >
                          İptal
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Sağ menü */}
      <div style={styles.sidebar}>
        <h3 style={styles.sidebarHeading}>Hızlı Erişim</h3>
        <button
          style={styles.menuButton}
          onClick={() => navigate("/add-project")}
        >
          Proje Ekle
        </button>
        <button
          style={styles.menuButton}
          onClick={() => navigate("/company_projects")}
        >
          Projelerim
        </button>
        <button
          style={styles.menuButton}
          onClick={() => navigate("/payment")}
        >
          Ödeme
        </button>
        <button
          style={styles.menuButton}
          onClick={() => navigate("/company_update_password")}
        >
          Şifre Değiştir
        </button>
        <button
          style={styles.menuButton}
          onClick={() => navigate("/company_update_logo")}
        >
          Logo Değiştir
        </button>
        <div style={styles.sidebarNote}>
          <span style={styles.sidebarNoteIcon}>💡</span>
          Profilinizi tamamlayın, eksik bilgileriniz varsa doldurun.
        </div>
      </div>
    </div>
  );
}

// 🎨 Styles
const styles = {
  mainContainer: {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
    maxWidth: "1200px",
    margin: "16px auto",
    padding: "0 12px",
    alignItems: "stretch",
  },
  container: {
    flex: 2,
    padding: "16px",
    backgroundColor: "#f7f9fb",
    borderRadius: "10px",
    boxShadow: "0 8px 30px rgba(8,30,70,0.04)",
    fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
    width: "100%",
  },
  loadingContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    minHeight: "300px",
  },
  spinner: {
    width: "40px",
    height: "40px",
    border: "4px solid #f3f3f3",
    borderTop: "4px solid #0b63d6",
    borderRadius: "50%",
    animation: "spin 1s linear infinite",
  },
  loadingText: {
    marginTop: "16px",
    color: "#666",
  },
  errorContainer: {
    textAlign: "center",
    padding: "40px 20px",
  },
  errorText: {
    fontSize: "16px",
    color: "#dc2626",
    marginBottom: "20px",
  },
  backButton: {
    backgroundColor: "#0b63d6",
    color: "white",
    border: "none",
    padding: "10px 20px",
    borderRadius: "6px",
    cursor: "pointer",
    fontSize: "14px",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: "20px",
    paddingBottom: "15px",
    borderBottom: "1px solid #e5e7eb",
  },
  headerInfo: {
    flex: 1,
  },
  heading: {
    fontSize: "20px",
    color: "#0b2b5a",
    marginBottom: "12px",
    textAlign: "left",
  },
  companyInfo: {
    display: "flex",
    flexDirection: "column",
    gap: "6px",
  },
  infoRow: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  infoLabel: {
    fontSize: "13px",
    color: "#6b7280",
    fontWeight: "500",
  },
  infoValue: {
    fontSize: "13px",
    color: "#111827",
    fontWeight: "500",
  },
  logoutButton: {
    backgroundColor: "#dc2626",
    color: "white",
    border: "none",
    padding: "8px 16px",
    borderRadius: "6px",
    cursor: "pointer",
    fontSize: "13px",
    fontWeight: "500",
    transition: "background-color 0.2s",
    whiteSpace: "nowrap",
  },
  infoBanner: {
    display: "flex",
    alignItems: "flex-start",
    gap: "8px",
    fontSize: "12px",
    backgroundColor: "#f0f9ff",
    color: "#0369a1",
    padding: "10px 12px",
    borderRadius: "6px",
    marginBottom: "16px",
    border: "1px solid #bae6fd",
    flexWrap: "wrap",
  },
  infoIcon: { 
    fontSize: "14px", 
    color: "#0369a1", 
    opacity: 0.8,
    flexShrink: 0,
  },
  personSection: {
    marginBottom: "24px",
  },
  section: {
    marginBottom: "24px",
  },
  sectionTitle: {
    fontSize: "16px",
    fontWeight: "700",
    color: "#0b2b5a",
    marginBottom: "12px",
    paddingBottom: "8px",
    borderBottom: "2px solid #e5e7eb",
  },
  sectionSubtitle: {
    fontSize: "14px",
    fontWeight: "600",
    color: "#4b5563",
    marginBottom: "8px",
  },
  personInfoBox: {
    backgroundColor: "#fff",
    borderRadius: "8px",
    padding: "16px",
    marginBottom: "12px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
    border: "1px solid #e5e7eb",
  },
  personField: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "8px 0",
    borderBottom: "1px solid #f3f4f6",
  },
  personLabel: {
    fontSize: "13px",
    color: "#6b7280",
    fontWeight: "500",
    flex: 1,
  },
  personValue: {
    fontSize: "13px",
    color: "#111827",
    fontWeight: "400",
    flex: 2,
    textAlign: "right",
  },
  note: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    fontSize: "11px",
    color: "#6b7280",
    fontStyle: "italic",
  },
  noteIcon: {
    fontSize: "12px",
  },
  infoBox: {
    backgroundColor: "#fff",
    borderRadius: "8px",
    padding: "16px",
    boxShadow: "0 4px 10px rgba(9,30,66,0.03)",
    lineHeight: 1.5,
  },
  fieldRow: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    padding: "10px 12px",
    borderRadius: "6px",
    marginBottom: "8px",
    cursor: "pointer",
    gap: "8px",
    transition: "background-color 0.2s",
  },
  labelWrap: { 
    display: "flex", 
    alignItems: "center", 
    gap: "8px",
    width: "100%",
  },
  label: { 
    fontSize: "13px", 
    color: "#4b5563", 
    fontWeight: 600,
    minWidth: "100px",
  },
  dirtyPill: {
    fontSize: "11px",
    backgroundColor: "#fff4e5",
    color: "#b45309",
    padding: "3px 8px",
    borderRadius: "999px",
    border: "1px solid rgba(180,83,9,0.08)",
    flexShrink: 0,
  },
  valueWrap: {
    width: "100%",
    display: "flex",
    justifyContent: "flex-start",
    alignItems: "center",
  },
  valueText: { 
    fontSize: "13px", 
    color: "#111827", 
    textAlign: "left",
    wordBreak: "break-word",
    width: "100%",
  },
  emptyText: {
    color: "#9ca3af",
    fontStyle: "italic",
  },
  inlineInput: {
    fontSize: "13px",
    padding: "6px 10px",
    borderRadius: "5px",
    border: "1px solid #d1d5db",
    outline: "none",
    width: "100%",
    transition: "border-color 0.2s",
  },
  updateArea: { 
    marginTop: "20px",
    width: "100%",
  },
  updateButton: {
    padding: "10px 16px",
    borderRadius: "6px",
    border: "none",
    backgroundColor: "#0b63d6",
    color: "#fff",
    fontWeight: 600,
    cursor: "pointer",
    fontSize: "14px",
    width: "100%",
    transition: "all 0.2s",
  },
  verificationBox: {
    marginTop: "12px",
    padding: "12px",
    borderRadius: "6px",
    border: "1px dashed rgba(10,20,60,0.06)",
    backgroundColor: "#fcfeff",
    width: "100%",
  },
  verificationRow: {
    display: "flex",
    flexDirection: "column",
    gap: "12px",
    alignItems: "stretch",
  },
  verificationCodeSection: {
    flex: 1,
  },
  verificationInputSection: {
    display: "flex",
    flexDirection: "column",
  },
  codeBadge: {
    marginTop: "8px",
    padding: "8px 12px",
    borderRadius: "6px",
    backgroundColor: "#eef2ff",
    color: "#3730a3",
    fontWeight: 800,
    fontSize: "15px",
    letterSpacing: "1px",
    display: "inline-block",
    textAlign: "center",
    fontFamily: "monospace",
  },
  codeInput: {
    marginTop: "8px",
    padding: "8px 12px",
    border: "1px solid #cbd5e1",
    borderRadius: "5px",
    fontSize: "14px",
    width: "100%",
  },
  verificationButtons: {
    marginTop: "8px", 
    display: "flex", 
    gap: "8px",
    flexDirection: "column",
  },
  confirmButton: {
    backgroundColor: "#15803d",
    color: "#fff",
    padding: "8px 12px",
    borderRadius: "5px",
    border: "none",
    fontWeight: 600,
    cursor: "pointer",
    flex: 1,
    fontSize: "13px",
    transition: "opacity 0.2s",
  },
  cancelButton: {
    backgroundColor: "#e11d48",
    color: "#fff",
    padding: "8px 12px",
    borderRadius: "5px",
    border: "none",
    fontWeight: 600,
    cursor: "pointer",
    flex: 1,
    fontSize: "13px",
  },
  smallNote: {
    fontSize: "12px",
    color: "#6b7280",
  },
  sidebar: {
    flex: 1,
    backgroundColor: "#ffffff",
    borderRadius: "10px",
    padding: "16px",
    boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
    height: "fit-content",
    width: "100%",
    order: -1,
  },
  sidebarHeading: {
    fontSize: "16px",
    fontWeight: 700,
    color: "#0b2b5a",
    marginBottom: "12px",
    textAlign: "center",
  },
  menuButton: {
    display: "block",
    width: "100%",
    textAlign: "center",
    backgroundColor: "#f3f4f6",
    color: "#111827",
    border: "none",
    padding: "10px 12px",
    borderRadius: "6px",
    marginBottom: "8px",
    fontWeight: 500,
    cursor: "pointer",
    transition: "all 0.2s ease",
    fontSize: "13px",
  },
  sidebarNote: {
    marginTop: "12px",
    padding: "8px",
    fontSize: "11px",
    color: "#6b7280",
    backgroundColor: "#f9fafb",
    borderRadius: "6px",
    display: "flex",
    alignItems: "center",
    gap: "6px",
  },
  sidebarNoteIcon: {
    fontSize: "12px",
  },
  link: {
    color: "#0b63d6",
    textDecoration: "none",
    wordBreak: "break-all",
    fontSize: "13px",
  },
};

// Spinner animation için CSS
const styleSheet = document.createElement('style');
styleSheet.textContent = `
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;
document.head.appendChild(styleSheet);